import { useState } from 'react'
import { ChevronRight, History } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import type { VariantProps } from 'class-variance-authority'
import { badgeVariants } from '@/components/ui/badge'
import { SectionLabel } from '@/frontend/features/Home/SectionLabel'
import { PanelEmptyState } from '@/frontend/components/EmptyState'
import type { ProcessExecution, ProcessExecutionsPayload, ExecutionsEmptyReason } from './types'

type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>['variant']>

const STATUS_VARIANT: Record<string, BadgeVariant> = {
  ok: 'success',
  warning: 'warning',
  error: 'critical',
}

// Frase de vazio honesta por motivo (INVESTIGACAO-DADOS: o que não dá pra
// saber, a UI diz — nunca inventa execução).
const EMPTY_MESSAGE: Record<Exclude<ExecutionsEmptyReason, null>, string> = {
  no_link: 'Vincule uma rotina para ver as execuções deste processo aqui.',
  instance_down: 'A instância do colaborador está indisponível, as execuções aparecem quando ela voltar.',
  no_executions: 'Ainda sem execuções. Quando as rotinas vinculadas rodarem, aparecem aqui.',
}

// Título honesto por motivo: "ainda" só cabe em no_executions; nos outros o
// estado não é "esperar rodar", é "falta configurar" / "instância fora".
const EMPTY_TITLE: Record<Exclude<ExecutionsEmptyReason, null>, string> = {
  no_link: 'Nenhuma rotina vinculada',
  instance_down: 'Instância indisponível',
  no_executions: 'Nada executado ainda',
}

// "3m 12s" / "48s" / "1h 04m" a partir de segundos. Nulo → "—".
function formatDuration(seconds: number | null): string {
  if (seconds == null) return '—'
  if (seconds < 60) return `${seconds}s`
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  if (mins < 60) return secs > 0 ? `${mins}m ${String(secs).padStart(2, '0')}s` : `${mins}m`
  const hours = Math.floor(mins / 60)
  const remMins = mins % 60
  return `${hours}h ${String(remMins).padStart(2, '0')}m`
}

function formatCost(cost: number | null): string {
  if (cost == null) return '—'
  return `R$ ${cost.toFixed(2).replace('.', ',')}`
}

// "08 jul · 08:47" a partir do início da execução (ou último evento).
function formatWhen(iso: string | null): string {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  const day = date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }).replace('.', '')
  const time = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  return `${day} · ${time}`
}

function ExecutionRow({ execution }: { execution: ProcessExecution }) {
  const [open, setOpen] = useState(false)
  const when = formatWhen(execution.started_at ?? execution.last_event_at)
  const output = execution.output
  const status = execution.status

  return (
    <li className="overflow-hidden rounded-xl border border-border bg-card">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors duration-150 hover:bg-accent"
      >
        <span className="w-[92px] flex-none font-mono text-[12px] font-medium text-foreground">{when}</span>
        <span className="flex flex-1 items-center gap-3 text-[11.5px] text-faint-foreground">
          <span className="font-mono">{formatDuration(execution.duration_seconds)}</span>
          <span className="font-mono">{formatCost(execution.total_cost)}</span>
        </span>
        {status && (
          <Badge variant={STATUS_VARIANT[status.kind] ?? 'info'} className="flex-none">
            {status.label}
          </Badge>
        )}
        <ChevronRight
          className={`size-4 flex-none text-faint-foreground transition-transform duration-150 ${open ? 'rotate-90' : ''}`}
          aria-hidden
        />
      </button>

      {open && (
        <div className="border-t border-border px-4 pb-4 pt-3">
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-faint-foreground">
            Output gerado
          </p>
          {output?.summary ? (
            <div className="rounded-lg border border-border bg-muted px-3.5 py-3 text-[13px] leading-[1.6] text-foreground">
              <p className="whitespace-pre-wrap">{output.summary}</p>
            </div>
          ) : (
            <div className="rounded-lg border border-border bg-muted px-3.5 py-3 text-[13px] text-muted-foreground">
              Sem resumo extraível desta execução.
            </div>
          )}
        </div>
      )}
    </li>
  )
}

/**
 * Coluna principal do detalhe do processo (Onda C): as EXECUÇÕES reais —
 * fleet_sessions channel=cron cujo label casa com o nome das rotinas
 * vinculadas. Cada linha: quando · duração · custo · chip de resultado (só se
 * derivável) e expande para o OUTPUT integral (bg-muted). Honestidade total:
 * sem rotina vinculada / instância fora / sem sessão → frase de vazio.
 */
export function ExecutionsPanel({ executions }: { executions: ProcessExecutionsPayload }) {
  const { items, empty_reason } = executions

  return (
    <section aria-label="Execuções" className="space-y-2">
      <SectionLabel>
        Execuções
        {items.length > 0 && (
          <span className="rounded-full bg-secondary px-[7px] text-[10.5px] font-semibold text-muted-foreground">
            {items.length}
          </span>
        )}
      </SectionLabel>

      {items.length === 0 ? (
        <PanelEmptyState
          icon={History}
          framed={false}
          title={EMPTY_TITLE[empty_reason ?? 'no_executions']}
          description={EMPTY_MESSAGE[empty_reason ?? 'no_executions']}
        />
      ) : (
        <ul className="space-y-2.5">
          {items.map((execution) => (
            <ExecutionRow key={execution.id} execution={execution} />
          ))}
        </ul>
      )}
    </section>
  )
}
